import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HrWorkflowCommand, Person } from '@vigeo/shared';
import { fetchHrWorkflow, performHrWorkflow } from './api.js';

export type NewCommand = HrWorkflowCommand extends infer C ? C extends HrWorkflowCommand ? Omit<C, 'operationId'> : never : never;
export type WorkflowRun = (command: NewCommand) => Promise<void>;

/**
 * The person's workflow read and the one write path to it, shared by the panel and by Offer and
 * stage so both refresh the same cache the same way (#440). The query key is the one the person
 * page already reads, so every consumer shares a single fetch.
 */
export function useHrWorkflow(person: Person) {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['hr-workflow', person.id], queryFn: () => fetchHrWorkflow(person.id) });
  const mutation = useMutation({
    mutationFn: (command: HrWorkflowCommand) => performHrWorkflow(person.id, command),
    onSuccess: async (data, command) => {
      if (command.action === 'save-settings') await Promise.all([client.invalidateQueries({ queryKey: ['checklist'] }), client.invalidateQueries({ queryKey: ['hr-workflow'] }), client.invalidateQueries({ queryKey: ['dashboard'] })]);
      client.setQueryData(['hr-workflow', person.id], data);
      await Promise.all([client.invalidateQueries({ queryKey: ['checklist', person.id] }), client.invalidateQueries({ queryKey: ['audit', person.id] }), client.invalidateQueries({ queryKey: ['review'] })]);
    },
    onError: async () => { await client.invalidateQueries({ queryKey: ['hr-workflow', person.id] }); },
  });
  const run: WorkflowRun = async (command) => { await mutation.mutateAsync({ ...command, operationId: crypto.randomUUID() } as HrWorkflowCommand); };
  return { query, mutation, run };
}
